import React, { useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import ContentLoader, { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import { compareDatesAsc } from '@helpers/dateUtils';
import { useEntriesQuery, BookkeepingEntry } from '@api/queries/inventory';
import { useBookkeepingFilters } from '@hooks/useBookkeepingFilters';
import { EntryType } from '~types/types';
import Modes from '../../Common/InventoryNavigation';
import EntryFilters from './EntryFilters';
import EntryTable from './EntryTable';

const BookkeepingList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get filters from URL search params
  const { filters, tankFilter, dateFilter, setTankFilter, setDateFilter } = useBookkeepingFilters();

  // Fetch entries using TanStack Query
  const { data: entries = [], isLoading, isFetching, refetch } = useEntriesQuery({
    terminalId: filters.terminalId,
    from: filters.startDate
  });

  // Filter entries by tank and date range (client-side filtering)
  const filteredEntries = useMemo(() => {
    return entries.filter((entry: BookkeepingEntry) => {
      const { terminalId, tankId, startDate, endDate } = filters;

      return (
        (!tankId || entry.tankId === tankId) &&
        (!terminalId || entry.terminalId === terminalId) &&
        // Start filter should not filter out entries without date as they are likely very new
        (!startDate || compareDatesAsc(entry.order, startDate) >= 0) &&
        (!endDate || compareDatesAsc(entry.order, endDate) <= 0)
      );
    });
  }, [entries, filters]);

  // Calculate external filters key for table state management
  const filtersKey = useMemo(() => {
    const { terminalId, tankId, startDate, endDate } = filters;
    const combined = (terminalId || '') + (tankId || '') + (startDate || '') + (endDate || '');
    return combined.length > 0 ? combined : null;
  }, [filters]);

  const openEntry = useCallback(
    (entry: BookkeepingEntry) => {
      // Pass current search params as state so they can be restored when returning
      const returnState = { returnSearch: location.search };

      if (entry.entryType === EntryType.Measurement) {
        // Navigate to measurements page with tank filter in URL params
        const params = new URLSearchParams();
        params.set('tank', entry.tankId);
        navigate(`/bookkeeping/measurements?${params.toString()}`, { state: returnState });
      } else {
        navigate(`/bookkeeping/entry/${entry.id}`, { state: returnState });
      }
    },
    [navigate, location.search]
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const showLoader = filteredEntries.length === 0 && isLoading;
  const isRefreshing = filteredEntries.length > 0 && isFetching;

  return (
    <>
      <Modes isEnabled={true} fetch={handleRefresh} isLoading={isRefreshing} />
      <EntryFilters
        tankFilter={tankFilter}
        dateFilter={dateFilter}
        onTankFilterChange={setTankFilter}
        onDateFilterChange={setDateFilter}
      />
      <ContentContainer>
        {showLoader && <ContentLoader />}
        <EntryTable
          externalFiltersKey={filtersKey}
          entries={filteredEntries}
          onEntrySelected={openEntry}
        />
      </ContentContainer>
    </>
  );
};

export default BookkeepingList;
